import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, exhaustMap, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';

import { UserViewModel } from '@ninety/mobile/platform/api';
import {
  capacitorAppActions,
  CurrentUserSelectors,
  FeatureFlagActions,
  NavigationService,
  NotificationActions,
  OneSignalActions,
  UserListStateActions,
} from '@ninety/mobile/platform/state';

import { NinetyNotification } from '../models/ninety-notification.model';
import { NotificationItemType } from '../models/notification-item-type';
import { NotificationFeedApiService } from '../services/notification-feed-api.service';

import { NotificationFeedActions } from './notification-feed-state.actions';
import { NotificationFeedSelectors } from './notification-feed-state.selectors';

@Injectable()
export class NotificationFeedStateEffects {
  private readonly actions$ = inject(Actions);
  private readonly notificationFeedApiService = inject(NotificationFeedApiService);
  private readonly store = inject(Store);
  private readonly navigationService = inject(NavigationService);

  /**
   * Dispatch init once an *identified* flag update lands — the session can carry a token. The
   * sign-out re-anonymize flows through the same action and must not fire a tokenless request.
   */
  loadInitialFeed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeatureFlagActions.updatedFlagsProcessed),
      filter(({ context }) => context === 'identified'),
      map(() => NotificationFeedActions.init())
    )
  );

  /** Re-check for unread notifications when the app resumes from background. */
  recheckOnAppResume$ = createEffect(() =>
    this.actions$.pipe(
      // `appResumed` rather than `appStateChanged`: the broader event also fires for interruptions that never
      // backgrounded the app
      ofType(capacitorAppActions.appResumed),
      map(() => NotificationFeedActions.init())
    )
  );

  /** Background init — fetch notifications and compute the unread badge indicator. */
  initFeed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.init),
      concatLatestFrom(() => this.store.select(CurrentUserSelectors.selectUser)),
      switchMap(([, user]) =>
        this.notificationFeedApiService.getNotificationsInit().pipe(
          map(resp => {
            const unreadStatusIndicator = this.computeUnreadStatusIndicator(resp.notifications, user);
            return NotificationFeedActions.initSuccess({ resp, unreadStatusIndicator });
          }),
          catchError(error => of(NotificationFeedActions.getUserNotificationsFailure({ error })))
        )
      )
    )
  );

  /** Sync the badge indicator from the action payload to platform state. */
  syncStatusIndicator$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.initSuccess),
      map(({ unreadStatusIndicator }) => NotificationActions.setUnreadIndicator({ show: unreadStatusIndicator }))
    )
  );

  /** Show the unread badge when a push notification arrives in the foreground. */
  showBadgeOnPushReceived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OneSignalActions.pushNotificationReceived),
      map(() => NotificationActions.setUnreadIndicator({ show: true }))
    )
  );

  /** Load page 1 with the current filter — on open, refresh, or filter change. */
  loadFeed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        NotificationFeedActions.open,
        NotificationFeedActions.refreshFeed,
        NotificationFeedActions.setFilterOption
      ),
      concatLatestFrom(() => this.store.select(NotificationFeedSelectors.selectFilterBy)),
      switchMap(([action, filterBy]) => {
        const event = (action as { event?: CustomEvent }).event;
        return this.notificationFeedApiService.getNotifications(filterBy).pipe(
          map(resp => NotificationFeedActions.getUserNotificationsSuccess({ resp, ...(event && { event }) })),
          catchError(error =>
            of(NotificationFeedActions.getUserNotificationsFailure({ error, ...(event && { event }) }))
          )
        );
      })
    )
  );

  /** Complete the refresher event after data loads or errors. */
  completeRefreshEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          NotificationFeedActions.getUserNotificationsSuccess,
          NotificationFeedActions.getUserNotificationsFailure
        ),
        tap(({ event }) => (event?.target as HTMLIonRefresherElement | undefined)?.complete())
      ),
    { dispatch: false }
  );

  /** Update the current user in state so badge computation stays correct for future polling. */
  updateUserLastOpenedNotificationFeedAt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.open),
      map(() =>
        UserListStateActions.updateLoggedInUser({
          update: { lastOpenedNotificationFeedAt: new Date() },
        })
      )
    )
  );

  hideNotificationStatusIndicator$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.open),
      map(() => NotificationActions.setUnreadIndicator({ show: false }))
    )
  );

  /** Infinite scroll — load the next page. Ignores new requests while one is in-flight. */
  loadMoreNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.loadMoreNotifications),
      concatLatestFrom(() => this.store.select(NotificationFeedSelectors.selectPageAndFilter)),
      exhaustMap(([{ event }, { filterBy, currentPage }]) =>
        this.notificationFeedApiService.getNotifications(filterBy, currentPage + 1).pipe(
          map(resp => NotificationFeedActions.loadMoreNotificationsSuccess({ resp, event })),
          catchError(error => of(NotificationFeedActions.loadMoreNotificationsFailure({ error, event })))
        )
      )
    )
  );

  /** Complete the infinite scroll event after data loads or errors. */
  completeScrollEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          NotificationFeedActions.loadMoreNotificationsSuccess,
          NotificationFeedActions.loadMoreNotificationsFailure
        ),
        tap(({ event }) => event?.target?.complete())
      ),
    { dispatch: false }
  );

  /** Dispatch either mark-as-read or mark-as-unread based on the current read state. */
  toggleNotificationReadStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.toggleNotificationReadStatus),
      map(({ notification }) =>
        notification.markedReadAt
          ? NotificationFeedActions.markNotificationAsUnread({ id: notification.id })
          : NotificationFeedActions.markNotificationsAsRead({ ids: [notification.id] })
      )
    )
  );

  /** Fire-and-forget API call to mark notifications as read. */
  markNotificationsAsRead$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationFeedActions.markNotificationsAsRead),
        mergeMap(({ ids }) =>
          this.notificationFeedApiService.markNotificationsAsRead(ids).pipe(catchError(() => EMPTY))
        )
      ),
    { dispatch: false }
  );

  /** Fire-and-forget API call to mark a notification as unread. */
  markNotificationAsUnread$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationFeedActions.markNotificationAsUnread),
        mergeMap(({ id }) => this.notificationFeedApiService.markNotificationAsUnread(id).pipe(catchError(() => EMPTY)))
      ),
    { dispatch: false }
  );

  /** Fire-and-forget API call to mark all notifications as read. */
  markAllAsRead$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationFeedActions.markAllAsRead),
        mergeMap(() => this.notificationFeedApiService.markAllAsRead().pipe(catchError(() => EMPTY)))
      ),
    { dispatch: false }
  );

  /** Navigate to the item detail view based on the notification's item type. */
  openItemDetail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationFeedActions.openItemDetail),
        tap(({ notification }) => {
          switch (notification.itemType) {
            case NotificationItemType.todo:
              this.navigationService.goToTodoDetail(notification.itemId);
              break;
            case NotificationItemType.rock:
              this.navigationService.goToRockDetail(notification.itemId);
              break;
            case NotificationItemType.milestone:
              this.navigationService.goToMilestoneDetail(notification.itemId);
              break;
            case NotificationItemType.shortTermIssue:
            case NotificationItemType.longTermIssue:
              this.navigationService.goToIssueDetail(notification.itemId);
              break;
            case NotificationItemType.headline:
            case NotificationItemType.cascadingMessage:
              this.navigationService.goToHeadlineDetail(notification.itemId);
              break;
          }
        })
      ),
    { dispatch: false }
  );

  /** Fire-and-forget API calls to track the opened notification and mark it as read. */
  markAsReadAfterOpeningDetail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationFeedActions.openItemDetail),
        mergeMap(({ notification }) => this.notificationFeedApiService.markNotificationAsOpened(notification.id))
      ),
    { dispatch: false }
  );

  /**
   * Determines if there are unread notifications created after the user last opened the feed.
   * Used to show the unread badge indicator on the toolbar bell.
   */
  private computeUnreadStatusIndicator(
    notifications: NinetyNotification[] | null | undefined,
    user: UserViewModel | null | undefined
  ): boolean {
    if (!user || !notifications?.length) {
      return false;
    }

    // If the user has never opened the feed, show the badge if there are any unread notifications.
    if (!user.lastOpenedNotificationFeedAt) {
      return notifications.some(notification => !notification.markedReadAt);
    }

    const lastOpenedDate = new Date(user.lastOpenedNotificationFeedAt);

    return notifications.some(
      notification => !notification.markedReadAt && new Date(notification.createdAt) > lastOpenedDate
    );
  }
}
